import { apiClient } from './apiClient';

/**
 * One plan's post-op record, as the patient chart tab reads and writes it.
 *
 * Mirrors api/app/Http/Controllers/V5/PostOpsWriteController.php. Both the
 * `post ops` module and legacy permission 7 are enforced server-side on the
 * read as well as the writes.
 */

export interface PostOpsRecord {
  /** null until someone saves for the first time. */
  postopRequestId: number | null;
  /** false for a plan nobody has opened yet — the tab still renders. */
  exists: boolean;
  status: number | null;
  statusLabel: string;
  postopNote: string;
  isQa: boolean;
  qaNote: string;
  qaResolved: boolean;
  qaResolvedAt: string | null;
  reasonIds: number[];
  /** The denormalised joined titles the worklist displays. */
  reasons: string;
  updatedAt: string | null;
}

export interface PostOpsStatusChoice {
  value: number;
  label: string;
}

export interface PostOpsReasonChoice {
  id: number;
  title: string;
}

export interface PostOpsRecordResponse {
  planId: number;
  record: PostOpsRecord;
  reasonTypes: PostOpsReasonChoice[];
  statusChoices: PostOpsStatusChoice[];
}

/** The fields a general save may touch. */
export interface PostOpsRecordChanges {
  status?: number;
  postopNote?: string;
  wasQA?: number;
  qaNote?: string;
}

export function getPostOpsRecord(planId: number): Promise<PostOpsRecordResponse> {
  return apiClient
    .get<{ data: PostOpsRecordResponse }>(`/post-ops/${planId}`)
    .then((r) => r.data.data);
}

export function savePostOpsRecord(
  planId: number,
  changes: PostOpsRecordChanges,
): Promise<{ record: PostOpsRecord }> {
  return apiClient
    .patch<{ data: { record: PostOpsRecord } }>(`/post-ops/${planId}`, changes)
    .then((r) => r.data.data);
}

/**
 * Toggle the Q/A resolved flag.
 *
 * Separate from savePostOpsRecord because RE-OPENING a resolved case emails
 * everyone following the plan.
 */
export function setPostOpsQaResolved(
  planId: number,
  resolved: boolean,
): Promise<{ record: PostOpsRecord }> {
  return apiClient
    .put<{ data: { record: PostOpsRecord } }>(`/post-ops/${planId}/qa-resolved`, { resolved })
    .then((r) => r.data.data);
}

/**
 * Replace the reason selection. Send the WHOLE selected set — the server works
 * out what to add and remove.
 */
export function setPostOpsReasons(
  planId: number,
  reasonIds: number[],
): Promise<{ record: PostOpsRecord }> {
  return apiClient
    .put<{ data: { record: PostOpsRecord } }>(`/post-ops/${planId}/reasons`, { reasonIds })
    .then((r) => r.data.data);
}
